import { useCallback, useEffect, useState } from 'react';
import DocumentTitle from 'react-document-title';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { linkHoverEffect } from '@hivebrite/design-system/Theme';
import { toast } from '@hivebrite/design-system/Toast';

import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useVideoShareLayoutScale } from 'hb-react/shared/apps/Zoom/hooks/useVideoShareLayoutScale';
import { ConfirmationDialog } from 'hb-react/shared/components/ConfirmationDialog';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import { captureException } from 'hb-react/shared/utils/errorReporting';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { EVENT_TITLE_HEIGHT } from '../constants';

interface ZoomMeetingProps {
  leaveUrl: string;
  meetingData: {
    customerKey: string;
    meetingNumber: string;
    passWord: string;
    sdkKey: string;
    signature: string;
    userEmail: string;
    userName: string;
  };
  title: string;
}

export const ZoomMeeting = ({
  title,
  leaveUrl,
  meetingData,
}: ZoomMeetingProps) => {
  const { currentNetwork } = useDefaultAppContexts();
  const { setIsNetworkNavVisible, setIsBottomLinksAccordionVisible } =
    useUIContext();
  const [isConfirmLeaveDialogOpen, setIsConfirmLeaveDialogOpen] =
    useState(false);
  const navigate = useNavigate();
  const frameTheme = useFrameTheme();

  // Use the custom hook to handle scaling
  useVideoShareLayoutScale();

  const leaveMeetingAndGoToEventDetails = useCallback(() => {
    window.ZoomMtg?.leaveMeeting({
      success: () => {
        // This will force the ZoomCompleted page to redirect the user
        // back to the event details page.
        window.sessionStorage.removeItem('zoomMeetingEndReason');
      },
    });
  }, []);

  useEffect(() => {
    setIsNetworkNavVisible(false);
    setIsBottomLinksAccordionVisible(false);

    return () => {
      setIsNetworkNavVisible(true);
      setIsBottomLinksAccordionVisible(true);
    };
  }, [setIsNetworkNavVisible, setIsBottomLinksAccordionVisible]);

  useEffect(() => {
    // Add null check for window.ZoomMtg
    if (!window.ZoomMtg) {
      toast.error(__('Sorry, an error occurred'));

      navigate(leaveUrl);

      return;
    }

    if (!navigator.mediaDevices) {
      toast.error(
        s__(
          'Zoom|Camera and microphone access is required for Zoom meetings. Please ensure you are using HTTPS and have granted media permissions.',
        ),
      );

      navigate(leaveUrl);

      return;
    }

    window.ZoomMtg.preLoadWasm();
    window.ZoomMtg.prepareWebSDK();

    window.ZoomMtg.init({
      leaveUrl,
      patchJsMedia: true,
      leaveOnPageUnload: true,
      success: () => {
        window.ZoomMtg.inMeetingServiceListener('onUserLeave', (data) => {
          const { reasonCode } = data;

          try {
            const currentReason = window.sessionStorage.getItem(
              'zoomMeetingEndReason',
            );

            // Zoom will handle users being kicked by first registering the leave event with reason 6
            // then when the user clicks the "Exit" button in the dialog a new leave event with reason 0
            // is triggered. We want to use the original leave reason in cases like this.
            if (currentReason) {
              return;
            }

            window.sessionStorage.setItem(
              'zoomMeetingEndReason',
              String(reasonCode),
            );
          } catch {
            // Ignore storage errors
          }
        });

        window.ZoomMtg.join({
          signature: meetingData.signature,
          meetingNumber: meetingData.meetingNumber,
          passWord: meetingData.passWord,
          userName: meetingData.userName,
          userEmail: meetingData.userEmail,
          customerKey: meetingData.customerKey,
          sdkKey: meetingData.sdkKey,

          success: () => {},
          error: (error: unknown) => {
            toast.error(
              s__('Zoom|We were unable to connect you to the zoom meeting.'),
            );

            captureException(error);

            navigate(leaveUrl);
          },
        });
      },
      error: (error: unknown) => {
        toast.error(
          s__('Zoom|We were unable to connect you to the zoom meeting.'),
        );

        captureException(error);

        navigate(leaveUrl);
      },
    });

    return () => {
      leaveMeetingAndGoToEventDetails();
    };
  }, [meetingData, leaveUrl, navigate, leaveMeetingAndGoToEventDetails]);

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title,
        })}
      />
      <ThemeProvider
        theme={frameTheme.themeName}
        fontBrightness={frameTheme.fontBrightness}
      >
        <TitleBox
          font="h3-semibold"
          px={8}
          onClick={() => setIsConfirmLeaveDialogOpen(true)}
          bg={frameTheme.backgroundColor}
        >
          {title}
        </TitleBox>
      </ThemeProvider>
      <ConfirmationDialog
        isOpen={isConfirmLeaveDialogOpen}
        onDismiss={() => {
          setIsConfirmLeaveDialogOpen(false);
        }}
        header={s__("Zoom|You're about to leave the live event")}
        confirmButtonText={s__('Zoom|Leave anyway')}
        confirmButtonVariant="secondary"
        cancelButtonText={s__('Zoom|Stay in event')}
        cancelButtonVariant="primary"
        onConfirm={leaveMeetingAndGoToEventDetails}
        invertButtons
      >
        {s__(
          'Zoom|The event is still in progress. Are you sure you want to go back?',
        )}
      </ConfirmationDialog>
    </>
  );
};

const TitleBox = styled(Box).attrs(() => ({
  width: '100%',
  position: 'fixed',
  top: 0,
  left: 0,
  height: `${EVENT_TITLE_HEIGHT}px`,
  alignItems: 'center',
}))`
  cursor: pointer;
  text-decoration: none;
  ${linkHoverEffect}
`;
